import React, { useState, useMemo, useRef, useEffect } from 'react';
import { submitRefusalMessage } from './submitRefusal';
import { buildLiteFollowUps } from './liteSubmitReplay';
import {
  useParams,
  useNavigate,
  useBlocker,
  useSearchParams,
} from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { api } from '../../services/api';
import { sitesService } from '../../services/sitesService';
import { offlineStorage } from '../../services/offlineStorage';
import type { InspectionDraft } from '../../services/offlineStorage';
import { draftPlan } from './submittedDraft';
import { withoutOwnActions } from './ownCorrectiveActions';
import { applySameIssue, coveredQuestions } from './sameIssue';
import {
  foldPosition,
  hasPin,
  markFiled,
  markFirstAnswer,
  markLeft,
  timingPayload,
  type VisitTiming,
} from './visitTiming';
import { useArrivalWatch } from '../../hooks/useArrivalWatch';
import { useToast } from '@/hooks/useToast';
import { format } from 'date-fns';
import type { ProjectType } from '../../types/constructionStage';
import { useAuthStore } from '../../stores/authStore';
import { useOffline } from '../../hooks/useOffline';
import { useInspectionSchema } from '../../hooks/useInspectionSchema';
import { generateId } from '../../utils/id';
import {
  transformSchemaToCategories,
  isQuestionVisible,
  type InspectionCategory,
} from '../../utils/schemaQuestionMapper';
import { useReportGeneration } from './hooks/useReportGeneration';
import { useStageDateEditing } from './hooks/useStageDateEditing';
import { ReportPhase } from './components/ReportPhase';
import { QuestionsPhase } from './components/QuestionsPhase';
import { SignatureGate } from './components/SignatureGate';
import { BmpMapGate } from './BmpMapGate';
import { isBmpGateNeeded, type BmpGateStatus } from './bmpGate';
import { ProjectTypePicker } from './ProjectTypePicker';
import { isMissingProjectType } from './missingProjectType';
import { EstCompletionGate } from './EstCompletionGate';
import { applyStageIds, planStageCreates } from './stageWrites';
import { estCompletionPrompt } from './estCompletionPrompt';
import { RecipientsGate } from './RecipientsGate';
import { recipientsPromptNeeded } from './recipientsPrompt';
import { prePopulateResponses } from './prePopulateResponses';
import { conductedAtIso, localClock } from './conductedAt';
import { SiteDetailDrawer } from '../sites/components/SiteDetailDrawer';
import { inspectionService } from '../../services/inspectionService';
import {
  conditionsPayload,
  isAustinSite,
  type BmpConditions,
  type BmpTap,
} from '../../services/bmpLogService';
import {
  trackFlowEvent,
  newFlowSessionId,
} from '../../services/telemetryService';
import { track } from '../../services/telemetry';

// Type for individual corrective action items (IR-002)
// How long the after-send note stays on screen (Jim: "5 or 10 seconds").
const HOLD_NOTE_MS = 10_000;

export type CorrectiveActionItem = {
  id: string; // unique id for this action
  location?: string;
  notes?: string;
  deadline?: string;
};

type InspectionResponse = {
  questionId: string;
  value: any;
  photos?: string[];
  notes?: string; // kept for backward compatibility
  location?: string; // kept for backward compatibility
  requiresCorrection?: boolean;
  correctionDeadline?: string; // kept for backward compatibility
  correctiveActions?: CorrectiveActionItem[]; // new: multiple corrective actions (IR-002)
  // JIM-1008-06: this No is the same problem as another No in this visit;
  // that question carries the corrective action for both.
  sameAsQuestionId?: string;
  timestamp: string;
};

// Fallback categories for when schema is unavailable or loading
const FALLBACK_CATEGORIES: InspectionCategory[] = [];

export function LiteInspectionFlowPage() {
  const { siteId } = useParams();
  // Edit on the site screen reopens a finished, unsent inspection as
  // /lite/inspect/:siteId?edit=<inspection id> (Jim, Teams 2026-10-05).
  const [searchParams] = useSearchParams();
  const editInspectionId = searchParams.get('edit');
  // JIM-1005-01: opened from the Rain Event box — this visit IS the rain
  // event inspection; the bottom box then asks whether it is also the weekly
  // routine. Kept in state so a resumed draft remembers how it began.
  const [rainEventStart, setRainEventStart] = useState<boolean>(
    searchParams.get('rain') === '1'
  );
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isOnline } = useOffline();
  // JIM-1006-05: when this visit happened on the ground. opened_at is the
  // fallback arrival; the phone's position replaces it when it reaches the
  // site; first answer / filed / left are stamped as they happen.
  const [visitTiming, setVisitTiming] = useState<VisitTiming>(() => ({
    opened_at: new Date().toISOString(),
  }));

  // Pilot watch: one telemetry session per inspection sitting, and a
  // site_opened milestone when the flow mounts for a site.
  const flowSessionRef = useRef<string>(newFlowSessionId());
  // Anchor for the question_answered rollup's duration_ms on submit.
  const flowStartedAtRef = useRef<number>(Date.now());
  React.useEffect(() => {
    if (!siteId) return;
    trackFlowEvent('site_opened', {
      sessionId: flowSessionRef.current,
      siteId,
    });
    flowStartedAtRef.current = Date.now();
    track('inspection_started', { site_id: siteId });
  }, [siteId]);

  const [responses, setResponses] = useState<
    Record<string, InspectionResponse>
  >({});
  const [, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // F-Comment: universal Inspection Report Comment field. Surfaces in the
  // QuestionsPhase textarea, persists into the IndexedDB draft, and flows to
  // page 1 of the PDF via the API payload's report_comment field.
  const [reportComment, setReportCommentState] = useState<string>('');
  // JIM-0929-01/02: City of Austin notes + this visit's BMP taps (become
  // events dated by the report when it is filed). Austin sites only.
  const [austinNotes, setAustinNotes] = useState<string>('');
  const [austinTaps, setAustinTaps] = useState<BmpTap[]>([]);
  // The BMP condition lines changed this visit; filed with the report too.
  const [austinConditions, setAustinConditions] = useState<BmpConditions>({});
  // #265: inspector marks that this single visit also covers a required rain
  // event -> submits type "both" instead of "routine".
  const [coversRainEvent, setCoversRainEvent] = useState<boolean>(false);
  // What the filed inspection is called: a rain event, a routine, or one
  // visit covering both. The report and every list read it as "Rain Event"
  // / "Routine / Rain Event" (Jim 2026-10-06: never the word "both").
  const visitType = (): 'routine' | 'rainfall' | 'both' => {
    if (coversRainEvent) return 'both';
    return rainEventStart ? 'rainfall' : 'routine';
  };
  // Inspection details (Jim 2026-08-20): conducted date defaults to today
  // (local calendar date — en-CA gives YYYY-MM-DD) but is editable for
  // back-dating; final-inspection declaration flows to the report note.
  const [conductedDate, setConductedDate] = useState<string>(() =>
    new Date().toLocaleDateString('en-CA')
  );
  // JIM-0919-04: the time box defaults to when the inspection was opened and
  // stays editable, so a back-dated visit carries a real time to the footer.
  const [conductedTime, setConductedTime] = useState<string>(() =>
    localClock()
  );
  const [isFinalInspection, setIsFinalInspection] = useState<boolean>(false);
  // JIM-1027-11a: why the inspector called it final; the consultant reads it.
  const [finalInspectionNote, setFinalInspectionNote] = useState<string>('');
  // Jim 2026-08-30: send the report to the consultant with an alert.
  const [alertConsultant, setAlertConsultant] = useState<boolean>(false);
  // Scroll-to target when returning from summary to questions
  const [scrollToQuestionId] = useState<string | null>(null);

  // Site info drawer (slide-up panel hosting the full Site Detail page)
  const [showSiteDetailDrawer, setShowSiteDetailDrawer] = useState(false);

  // Open corrective actions from previous inspections.
  // Array per question_id — one entry per open finding (multi-CA expansion:
  // cloning produces N findings with the same source_checklist_question_id,
  // all N must render individually so the inspector can verify each location).
  const [openCorrectiveActions, setOpenCorrectiveActions] = useState<
    Record<
      string,
      Array<{
        findingId: string;
        notes: string;
        location: string;
        date: string;
      }>
    >
  >({});

  // Findings-based CAs (legacy data) in CorrectiveAction format for Step 2
  const [findingsBasedCAs, setFindingsBasedCAs] = useState<
    Array<{
      id: string;
      question_id: string;
      question_text: string;
      category: string;
      status: string;
      last_answer: boolean;
      last_condition: string;
      notes: string | null;
      location: string | null;
      priority: 'medium' | 'high' | null;
      deadline: string | null;
    }>
  >([]);

  // Tracks whether the inspector has actually interacted with the form. Pre-pop
  // effects rely on this flag to decide whether to replace their own prior
  // computation (the early-arriving sources land first and would otherwise win
  // the merge on a later run when more data is in hand).
  const hasUserInteractedRef = useRef(false);

  // CAs checked off as verified during this inspection (caId → resolution notes)
  const [verifiedCaIds, setVerifiedCaIds] = useState<Record<string, string>>(
    {}
  );
  // Local priority overrides while on CA review screen (caId → priority)
  const [caPriorities, setCaPriorities] = useState<
    Record<string, 'medium' | 'high' | null>
  >({});

  // Report generation state (extracted to hook)
  const [showReportStep, setShowReportStep] = useState(false);
  // Questions → report and Edit Answers → questions are phase changes on the
  // same URL, so ScrollRestoration never fires; without this the review screen
  // opens at the questions page's bottom-of-form offset.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [showReportStep]);
  // Signature gate: inspector must have a signature on file before the
  // questions phase (Jim, 2026-06-29). Flipped true once they confirm/sign.
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [submittedInspectionId, setSubmittedInspectionId] = useState<
    string | null
  >(null);
  // A different site on the same mounted page starts from scratch: no
  // inspection id, no review step — so nothing from the previous inspection
  // (least of all its "Sent to Client" state) can show for the new one.
  useEffect(() => {
    setSubmittedInspectionId(null);
    setShowReportStep(false);
  }, [siteId]);
  // Submit-flow trace. Was an on-screen debugStatus array shipped to
  // inspectors' phones during early pilot debugging; production diagnosis now
  // goes through flow_events telemetry, so this only logs in dev builds.
  const addDebug = (msg: string) => {
    if (import.meta.env.DEV) console.log('[LiteInspection]', msg);
  };
  const {
    isGeneratingReport,
    setIsGeneratingReport,
    reportUrl,
    setReportUrl,
    reportError,
    setReportError,
    isSendingReport,
    sendResult,
    sendHold,
    stopSend,
    generateReport,
    approveAndSend,
  } = useReportGeneration(submittedInspectionId, addDebug);

  // Fetch site details (includes current construction stage)
  const { data: siteData, isLoading: siteLoading } = useQuery({
    queryKey: ['site-details', siteId],
    queryFn: async () => {
      const response = await api.get<{
        id: string;
        name: string;
        client_name?: string | null;
        project_type: ProjectType | null;
        rainfall_inspection_required?: boolean;
        current_construction_stage: {
          id: string;
          stage_name: string;
          stage_order: number;
          project_type: string;
        } | null;
        estimated_end_date?: string | null;
        est_completion_confirmed_at?: string | null;
        permit_expiration_date?: string | null;
        coordinates?: { latitude: number; longitude: number } | null;
      }>(`/sites/${siteId}`);
      return response.data;
    },
    enabled: !!siteId,
  });

  // Past-due Est. Completion gate (Jim 8/9): once lapsed, the inspector must
  // set a new date before inspecting — same posture as the BMP-map gate.
  const [estGateDone, setEstGateDone] = useState(false);
  // Calendar-date compare: the value is midnight UTC, so an instant compare
  // would call "today" lapsed all day and "tomorrow" lapsed from 7 pm Central.
  // Jim 10/13: also when there is no date, or nobody confirmed it in 90 days.
  const estPrompt = estCompletionPrompt(siteData);
  const estGateNeeded = estPrompt !== null;

  // First-inspection recipients check (Jim 10/21): before a site's first
  // inspection filed in the app, show who the report will be emailed to,
  // once. The site's own count of app inspections ends it for good.
  const { data: recipientsData } = useQuery({
    queryKey: ['site-report-recipients', siteId],
    queryFn: () => sitesService.getReportRecipients(siteId!),
    enabled: !!siteId,
    retry: 1,
  });
  const [recipientsGateDone, setRecipientsGateDone] = useState(false);
  const recipientsGateNeeded = recipientsPromptNeeded(
    recipientsData,
    siteId ?? ''
  );

  // BMP-map gate (EP-SWPPP WS-4; cadence Jim 10/7): block the inspection
  // once four routine visits have been filed since the last map, until an
  // updated map is uploaded or overridden. A map can be several
  // sheets, so the gate stays mounted from the moment it's needed until the
  // inspector taps Continue — dismissing on `satisfied` would tear it down
  // after sheet 1 and strand the rest.
  const { data: bmpStatus } = useQuery({
    queryKey: ['bmp-map-status', siteId],
    queryFn: async () => {
      const r = await api.get<BmpGateStatus>(`/sites/${siteId}/bmp-map/status`);
      return r.data;
    },
    enabled: !!siteId,
  });
  const [bmpGateDone, setBmpGateDone] = useState(false);
  // Final-inspection BMP gate (Jim 2026-08-20): a declared final inspection
  // can't be submitted without a photo of the completed BMP map taken today.
  const [finalBmpGateOpen, setFinalBmpGateOpen] = useState(false);
  const [finalBmpDone, setFinalBmpDone] = useState(false);
  const bmpGateNeeded = isBmpGateNeeded(bmpStatus);
  const [bmpGateEngaged, setBmpGateEngaged] = useState(false);
  useEffect(() => {
    if (bmpGateNeeded) setBmpGateEngaged(true);
  }, [bmpGateNeeded]);

  // Fetch extracted stage observations for this site (for pre-populating dates)
  type ExtractedStageItem = {
    id: string;
    canonical_stage: string;
    stage_name: string;
    start_date_noted: string | null;
    completion_date_noted: string | null;
    created_at: string;
  };
  const { data: extractedStagesData } = useQuery({
    queryKey: ['extracted-stages-site', siteId],
    queryFn: async () => {
      const res = await api.get<{ items: ExtractedStageItem[] }>(
        `/extracted-stages?site_id=${siteId}&size=100`
      );
      return res.data.items || [];
    },
    enabled: !!siteId,
  });

  // Fetch schema for this site (with offline support).
  // Stages shown to the inspector come from this schema's schema_stage_questions,
  // so the admin's curated stage list drives what the inspector sees — not the
  // global construction_stages catalog (which can contain legacy/probe rows).
  const {
    schema,
    isLoading: schemaLoading,
    error: schemaError,
    refresh: refreshSchema,
    isFromCache,
  } = useInspectionSchema(siteId);

  const allStages = useMemo(() => {
    if (!schema?.stages?.length) return [];
    return schema.stages
      .filter((s) => s.construction_stage_id && s.construction_stage_name)
      .map((s) => ({
        id: s.construction_stage_id as string,
        stage_name: s.construction_stage_name as string,
        construction_stage_key: s.construction_stage_key ?? null,
        stage_order: s.sort_order,
        project_type: schema.project_type as string,
      }))
      .sort((a, b) => a.stage_order - b.stage_order);
  }, [schema]);
  const currentStage = siteData?.current_construction_stage;

  // Stage date editing (extracted to hook)
  const {
    selectedStageId,
    stageDates,
    setStageDates,
    handleStageDateSetAndSave,
    handleStageDateBlur,
  } = useStageDateEditing(extractedStagesData, { stages: allStages });

  // Fetch last inspection's full checklist answers (for historical display)
  type LastAnswer = {
    value: string | null;
    notes: string | null;
    findings: Array<{
      description: string;
      location: string | null;
      condition: string;
      is_resolved: boolean;
    }>;
  };
  const { data: lastAnswersData } = useQuery({
    queryKey: ['last-inspection-answers', siteId],
    queryFn: async () => {
      const resp = await api.get<{
        answers: Record<string, LastAnswer>;
        inspection_id: string | null;
      }>(`/sites/${siteId}/last-inspection-answers`);
      return resp.data;
    },
    enabled: !!siteId,
  });

  // Transform schema to inspection categories
  const inspectionCategories = useMemo(() => {
    if (!schema) return FALLBACK_CATEGORIES;
    return transformSchemaToCategories(schema);
  }, [schema]);

  // Fetch corrective actions for "LAST TIME" indicators (uses the switch-based tracking table)
  const { data: serverCorrectiveActions } = useQuery({
    queryKey: ['corrective-actions', siteId],
    queryFn: async () => {
      const response = await api.get<{
        site_id: string;
        corrective_actions: Array<{
          created_by_inspection_id?: string | null;
          id: string;
          question_id: string;
          question_text: string;
          category: string;
          status: string;
          last_answer: boolean;
          last_condition: string;
          notes: string | null;
          location: string | null;
          priority: 'medium' | 'high' | null;
          deadline: string | null;
        }>;
        open_count: number;
        resolved_count: number;
      }>(`/sites/${siteId}/corrective-actions`);
      return response.data;
    },
    enabled: !!siteId,
  });
  // In Edit the server already holds this visit's own corrective actions;
  // they are the form's answers, not open items from before (Jim 10/22).
  const correctiveActionsData = useMemo(
    () =>
      serverCorrectiveActions && editInspectionId
        ? {
            ...serverCorrectiveActions,
            corrective_actions: withoutOwnActions(
              serverCorrectiveActions.corrective_actions,
              editInspectionId
            )!,
          }
        : serverCorrectiveActions,
    [serverCorrectiveActions, editInspectionId]
  );

  // Fetch last inspection for legacy compatibility and pre-populating
  const { data: lastInspection } = useQuery({
    queryKey: ['last-inspection', siteId],
    queryFn: async () => {
      // Fetch recent inspections of ANY finished status. The old call used
      // status_filter=submitted only, so the moment an inspection was
      // APPROVED it vanished and pre-fill silently died — and on shared test
      // sites the newest "submitted" row can be an e2e husk with zero
      // findings (audit 2026-08-31).
      const listResponse = await api.get<{
        inspections?: any[];
        items?: any[];
      }>(`/sites/${siteId}/inspections?limit=8`);
      const inspections = (
        listResponse.data?.inspections ||
        listResponse.data?.items ||
        []
      )
        .filter(
          (i: any) =>
            ['submitted', 'approved', 'completed'].includes(i.status) &&
            i.conducted_date
        )
        .sort(
          (a: any, b: any) =>
            new Date(b.conducted_date).getTime() -
            new Date(a.conducted_date).getTime()
        );

      // Walk newest-first until one actually carries findings, so an empty
      // shell can't blank the pre-fill when a real prior visit exists.
      for (const candidate of inspections.slice(0, 3)) {
        const detailResponse = await api.get<any>(
          `/inspections/${candidate.id}`
        );
        const detail = detailResponse.data;
        if (detail?.findings?.length) {
          return detail;
        }
      }
      return null;
    },
    enabled: !!siteId,
  });

  // Pre-populate responses from last inspection. See prePopulateResponses.ts
  // for the merge contract across the three data sources. The effect re-fires
  // as each source lands; on every run we replace the pre-pop slice entirely
  // (instead of letting the earlier-arriving partial computation win) so a
  // stale "good" condition can't lock in Yes before the CA table reveals open
  // findings. User edits are preserved via hasUserInteractedRef.
  React.useEffect(() => {
    if (inspectionCategories.length === 0) return;
    if (
      !lastAnswersData?.answers &&
      !correctiveActionsData?.corrective_actions?.length &&
      !lastInspection
    ) {
      return;
    }

    const previousResponses = prePopulateResponses({
      lastAnswers: lastAnswersData?.answers,
      correctiveActions: correctiveActionsData?.corrective_actions,
      lastInspectionFindings: lastInspection?.findings,
      inspectionCategories,
    });

    if (Object.keys(previousResponses).length === 0) return;

    setResponses((prev) => {
      if (!hasUserInteractedRef.current) {
        // No user edits yet — the latest pre-pop is the source of truth.
        // This breaks the race where an early run (e.g. lastInspection only)
        // wrote Yes and a later run (with CA-table opens) couldn't override.
        return previousResponses as Record<string, InspectionResponse>;
      }
      // Preserve user-edited responses; fill blanks from the latest pre-pop.
      return {
        ...(previousResponses as Record<string, InspectionResponse>),
        ...prev,
      };
    });
  }, [
    lastAnswersData,
    correctiveActionsData,
    lastInspection,
    inspectionCategories,
  ]);

  // Build a map of previous answers for highlighting (LAST TIME indicators).
  // Primary: lastAnswersData (every Yes/No/NA from checklist_responses).
  // Overlay: corrective_actions table (authoritative for "No" state).
  // Fallback: lastInspection.findings (legacy pre-checklist_responses data).
  const previousAnswers = useMemo(() => {
    const answers: Record<string, boolean | string> = {};

    if (lastAnswersData?.answers) {
      Object.entries(lastAnswersData.answers).forEach(([qid, ans]) => {
        const raw = (ans.value || '').toLowerCase();
        if (raw === 'yes') answers[qid] = true;
        else if (raw === 'no') answers[qid] = false;
        else if (raw === 'na' || raw === 'n/a') answers[qid] = 'na';
      });
    }

    if (correctiveActionsData?.corrective_actions?.length) {
      correctiveActionsData.corrective_actions.forEach((action) => {
        if (answers[action.question_id] === undefined) {
          answers[action.question_id] = action.last_answer;
        }
      });
    }

    if (
      Object.keys(answers).length === 0 &&
      lastInspection?.findings &&
      Array.isArray(lastInspection.findings) &&
      inspectionCategories.length > 0
    ) {
      const allQuestions = inspectionCategories.flatMap((cat) => cat.questions);
      lastInspection.findings.forEach((finding: any) => {
        let matchingQuestion = finding.source_checklist_question_id
          ? allQuestions.find(
              (q) => q.id === finding.source_checklist_question_id
            )
          : null;
        if (!matchingQuestion) {
          matchingQuestion = allQuestions.find(
            (q) => q.question === finding.item
          );
        }
        if (matchingQuestion) {
          let value: boolean | string = finding.condition === 'good';
          if (
            finding.condition === 'poor' ||
            finding.condition === 'failed' ||
            finding.requires_correction
          ) {
            value = false;
          }
          answers[matchingQuestion.id] = value;
        }
      });
    }

    return answers;
  }, [
    lastAnswersData,
    correctiveActionsData,
    lastInspection,
    inspectionCategories,
  ]);

  // Populate open corrective actions — union of corrective_actions table and
  // the raw findings endpoint. Previously the findings endpoint was a fallback
  // that never fired when the CA table had any row, which hid orphaned open
  // findings on sites that were partially synced. Merging by question_id keeps
  // the CA table authoritative where present but catches the stragglers.
  React.useEffect(() => {
    const loadOpenActions = async () => {
      if (!siteId || inspectionCategories.length === 0) return;

      const openActions: Record<
        string,
        Array<{
          findingId: string;
          notes: string;
          location: string;
          date: string;
        }>
      > = {};
      const allQuestions = inspectionCategories.flatMap((cat) => cat.questions);
      const findingsCAs: typeof findingsBasedCAs = [];

      // Always fetch unresolved findings so we don't miss any open items
      try {
        const response = await api.get<{ findings: any[] }>(
          `/sites/${siteId}/findings?requires_correction=true&is_resolved=false`
        );
        const findings = response.data?.findings || [];

        findings.forEach((finding: any) => {
          let matchingQuestion = finding.source_checklist_question_id
            ? allQuestions.find(
                (q) => q.id === finding.source_checklist_question_id
              )
            : null;

          if (!matchingQuestion) {
            matchingQuestion = allQuestions.find(
              (q) => q.question === finding.item
            );
          }

          if (matchingQuestion) {
            const qid = matchingQuestion.id;
            if (!openActions[qid]) openActions[qid] = [];
            openActions[qid].push({
              findingId: finding.id,
              notes: finding.correction_notes || finding.description || '',
              location: finding.location || '',
              date: finding.created_at || '',
            });
            const matchingCategory = inspectionCategories.find((cat) =>
              cat.questions.some((q) => q.id === qid)
            );
            findingsCAs.push({
              id: finding.id,
              question_id: qid,
              question_text: matchingQuestion.question || finding.item || '',
              category: matchingCategory?.name || 'Unknown',
              status: 'open',
              last_answer: false,
              last_condition: finding.description || '',
              notes: finding.correction_notes || finding.description || '',
              location: finding.location || null,
              priority: null,
              deadline: finding.deadline || finding.created_at || null,
            });
          }
        });
      } catch (error) {
        console.error('[LiteInspection] Failed to fetch findings:', error);
      }
      setFindingsBasedCAs(findingsCAs);

      // Overlay the corrective_actions table. The table is the authoritative
      // record for what's still open per (question_id, location); the findings
      // endpoint may lag or carry stragglers. Treat each row as a finding-id
      // verdict, not a question-level switch:
      //   - resolved row → drop *that* findingId (don't blow away other opens
      //     on the same question — a Q with 5 opens + 1 resolved must still
      //     show 5 entries)
      //   - open / in_progress row → add to that question's list if its id
      //     isn't already present (dedupe against findings-endpoint entries)
      if (correctiveActionsData?.corrective_actions?.length) {
        const resolvedFindingIds = new Set<string>();
        correctiveActionsData.corrective_actions.forEach((action) => {
          if (action.status === 'resolved') {
            resolvedFindingIds.add(action.id);
            return;
          }
          if (action.status !== 'open' && action.status !== 'in_progress') {
            return;
          }
          const existing = openActions[action.question_id] || [];
          if (existing.some((e) => e.findingId === action.id)) return;
          openActions[action.question_id] = [
            ...existing,
            {
              findingId: action.id,
              notes: action.notes || '',
              location: action.location || '',
              date: action.deadline || '',
            },
          ];
        });
        if (resolvedFindingIds.size > 0) {
          Object.keys(openActions).forEach((qid) => {
            openActions[qid] = openActions[qid].filter(
              (e) => !resolvedFindingIds.has(e.findingId)
            );
            if (openActions[qid].length === 0) delete openActions[qid];
          });
        }
      }

      setOpenCorrectiveActions(openActions);
    };

    loadOpenActions();
  }, [siteId, correctiveActionsData, inspectionCategories]);

  // EP-TRANSITION: loose CAs — open rows whose question_id matches no schema
  // question (legacy-mirrored items carry a synthetic id). They surface in a
  // "Carried from legacy" bucket for verify-or-reparent; they never force a
  // question answer until reparented (Jim 2026-08-04).
  const queryClient = useQueryClient();
  const looseCAs = useMemo(() => {
    const schemaQuestionIds = new Set(
      inspectionCategories.flatMap((c) => c.questions.map((q) => q.id))
    );
    return (correctiveActionsData?.corrective_actions ?? []).filter(
      (ca) =>
        (ca.status === 'open' || ca.status === 'in_progress') &&
        !schemaQuestionIds.has(ca.question_id) &&
        !verifiedCaIds[ca.id]
    );
  }, [correctiveActionsData, inspectionCategories, verifiedCaIds]);

  const reparentLooseCa = async (caId: string, questionId: string) => {
    const ca = (correctiveActionsData?.corrective_actions ?? []).find(
      (c) => c.id === caId
    );
    await api.patch(`/sites/${siteId}/corrective-actions/${caId}/reparent`, {
      question_id: questionId,
    });
    // Jim: reparenting populates the question to No right away, carrying the
    // legacy location/description — no page refresh.
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        questionId,
        value: false,
        requiresCorrection: true,
        notes: ca?.notes || undefined,
        timestamp: new Date().toISOString(),
      },
    }));
    await queryClient.invalidateQueries({
      queryKey: ['corrective-actions', siteId],
    });
  };

  const verifyLooseCa = async (caId: string) => {
    await api.patch(`/sites/${siteId}/corrective-actions/${caId}/resolve`, {
      resolution_notes: 'Verified during inspection (carried from legacy)',
    });
    setVerifiedCaIds((prev) => ({
      ...prev,
      [caId]: 'Verified during inspection (carried from legacy)',
    }));
    await queryClient.invalidateQueries({
      queryKey: ['corrective-actions', siteId],
    });
  };

  // Pre-populate "No" responses for questions with open corrective actions.
  // This ensures inspectors see unresolved items pre-set to "No" with prior notes,
  // regardless of whether lastInspection findings matched via text.
  React.useEffect(() => {
    const openKeys = Object.keys(openCorrectiveActions);
    if (openKeys.length === 0) return;

    setResponses((prev) => {
      const updates: Record<string, InspectionResponse> = {};
      openKeys.forEach((questionId) => {
        if (prev[questionId] !== undefined) return; // already answered
        const actions = openCorrectiveActions[questionId];
        if (!actions?.length) return;
        const first = actions[0];
        updates[questionId] = {
          questionId,
          value: false,
          requiresCorrection: true,
          notes: first.notes || undefined,
          correctionDeadline: first.date || undefined,
          timestamp: new Date().toISOString(),
        };
      });
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, [openCorrectiveActions]);

  // Initialize offline storage
  React.useEffect(() => {
    offlineStorage.init().catch(console.error);
  }, []);

  // Draft resume: persist in-progress inspection state to IndexedDB so a
  // refresh / app-kill / crash doesn't wipe answers + CAs. Scoped per
  // (siteId, userId). Cleared on successful submit.
  const userId = useAuthStore((s) => s.user?.id);
  const [draftGate, setDraftGate] = useState<'loading' | 'prompt' | 'active'>(
    'loading'
  );
  const [pendingDraft, setPendingDraft] = useState<InspectionDraft | null>(
    null
  );
  // Edit was asked for, but this phone does not hold that inspection's answers.
  const [editUnavailable, setEditUnavailable] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Put saved answers back on the form: a resumed draft, or a finished
  // inspection reopened with Edit.
  const applyDraftPayload = (payload: InspectionDraft['payload']) => {
    setResponses(payload.responses as Record<string, InspectionResponse>);
    setVerifiedCaIds(payload.verifiedCaIds || {});
    setCaPriorities(payload.caPriorities || {});
    setReportCommentState(payload.reportComment || '');
    setCoversRainEvent(!!payload.coversRainEvent);
    setRainEventStart(!!payload.rainEventStart);
    setAustinNotes(payload.austinNotes || '');
    setAustinTaps((payload.austinTaps as BmpTap[]) || []);
    setAustinConditions(payload.austinConditions || {});
    if (payload.visitTiming) {
      setVisitTiming((t) => ({
        ...t,
        ...(payload.visitTiming as VisitTiming),
      }));
    }
    // The inspector was already working — any further change should keep
    // flowing into the draft, and pre-fill must only fill blanks.
    hasUserInteractedRef.current = true;
  };

  // Wrapped setters passed to QuestionsPhase; every user action routes through
  // these and flips hasUserInteractedRef. Pre-pop effects keep using the raw
  // setters so they don't accidentally trip the flag.
  const setResponsesFromUser: typeof setResponses = (v) => {
    hasUserInteractedRef.current = true;
    setVisitTiming((t) => markFirstAnswer(t, new Date().toISOString()));
    setResponses(v);
  };
  const setVerifiedCaIdsFromUser: typeof setVerifiedCaIds = (v) => {
    hasUserInteractedRef.current = true;
    setVerifiedCaIds(v);
  };
  const setCaPrioritiesFromUser: typeof setCaPriorities = (v) => {
    hasUserInteractedRef.current = true;
    setCaPriorities(v);
  };
  // F-Comment: setter wrapped to flip the interaction flag so the IndexedDB
  // draft picks up the comment as soon as the inspector types.
  const setReportCommentFromUser = (v: string) => {
    hasUserInteractedRef.current = true;
    setReportCommentState(v);
  };
  // The City of Austin section is the inspector's work as well: without the
  // flag, a visit where only this section was touched kept no draft.
  const setAustinNotesFromUser = (v: string) => {
    hasUserInteractedRef.current = true;
    setAustinNotes(v);
  };
  const setAustinTapsFromUser = (v: BmpTap[]) => {
    hasUserInteractedRef.current = true;
    setAustinTaps(v);
  };
  const setAustinConditionsFromUser = (v: BmpConditions) => {
    hasUserInteractedRef.current = true;
    setAustinConditions(v);
  };

  // On mount: look for a non-empty, non-stale draft and prompt to resume.
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!siteId || !userId) {
        setDraftGate('active');
        return;
      }
      try {
        await offlineStorage.init();
        const draft = await offlineStorage.getDraft(siteId, userId);
        if (cancelled) return;
        const plan = draftPlan(draft, editInspectionId);
        if (plan.kind === 'prompt') {
          setPendingDraft(plan.draft);
          setDraftGate('prompt');
          return;
        }
        if (plan.kind === 'reopen') {
          // The same inspection, as the inspector left it: its answers, its
          // date and time, and its id, so completing it again revises that
          // inspection instead of filing a second one.
          const filed = plan.draft.payload.submitted!;
          applyDraftPayload(plan.draft.payload);
          setConductedDate(filed.conductedDate);
          setConductedTime(filed.conductedTime);
          setIsFinalInspection(filed.isFinalInspection);
          setFinalInspectionNote(filed.finalInspectionNote || '');
          setAlertConsultant(filed.alertConsultant);
          setAustinNotes(filed.austinNotes);
          setAustinTaps(filed.austinTaps as BmpTap[]);
          setAustinConditions(filed.austinConditions || {});
          setSubmittedInspectionId(filed.inspectionId);
        }
        if (plan.kind === 'unavailable') setEditUnavailable(true);
        if (plan.kind === 'stale') {
          offlineStorage.clearDraft(siteId, userId).catch(() => {});
        }
        setDraftGate('active');
      } catch (err) {
        console.error('[LiteInspection] Draft load failed:', err);
        setDraftGate('active');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [siteId, userId, editInspectionId]);

  // What is kept on the phone. Once the answers are filed as an inspection
  // the draft carries that inspection's id and the parts of the form the
  // answers alone do not hold, and stays until the report is sent — that is
  // what Edit on the site screen reopens.
  const draftPayload = (
    filedAs: string | null
  ): InspectionDraft['payload'] => ({
    responses: responses as Record<string, unknown>,
    verifiedCaIds,
    caPriorities,
    reportComment,
    coversRainEvent,
    rainEventStart,
    austinNotes,
    austinTaps,
    austinConditions,
    visitTiming: visitTiming as Record<string, unknown>,
    ...(filedAs
      ? {
          submitted: {
            inspectionId: filedAs,
            conductedDate,
            conductedTime,
            isFinalInspection,
            finalInspectionNote,
            alertConsultant,
            austinNotes,
            austinTaps,
            austinConditions,
          },
        }
      : {}),
  });

  // Debounced draft save whenever responses / CA check-offs / priorities change.
  // Skipped until the user has decided resume-vs-fresh (draftGate === 'active')
  // AND has actually interacted — otherwise pre-pop from the last inspection
  // would immediately be persisted as a "draft" and Start-fresh would re-save it.
  React.useEffect(() => {
    if (draftGate !== 'active') return;
    if (!siteId || !userId) return;
    if (!hasUserInteractedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      offlineStorage
        .saveDraft(siteId, userId, draftPayload(submittedInspectionId))
        .catch((err) =>
          console.error('[LiteInspection] Draft save failed:', err)
        );
    }, 400);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
    // draftPayload reads the same state these deps cover.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    responses,
    verifiedCaIds,
    caPriorities,
    reportComment,
    coversRainEvent,
    rainEventStart,
    conductedDate,
    conductedTime,
    isFinalInspection,
    finalInspectionNote,
    alertConsultant,
    austinNotes,
    austinTaps,
    austinConditions,
    visitTiming,
    submittedInspectionId,
    siteId,
    userId,
    draftGate,
  ]);

  // The report has gone: the answers kept for Edit have done their job.
  useEffect(() => {
    if (sendResult && siteId && userId) {
      offlineStorage.clearDraft(siteId, userId).catch(() => {});
    }
  }, [sendResult, siteId, userId]);

  const handleResumeDraft = () => {
    if (!pendingDraft) {
      setDraftGate('active');
      return;
    }
    applyDraftPayload(pendingDraft.payload);
    setPendingDraft(null);
    setDraftGate('active');
  };

  const handleDiscardDraft = () => {
    if (siteId && userId) {
      offlineStorage.clearDraft(siteId, userId).catch(() => {});
    }
    // Fresh start — pre-pop may still be filling responses from server data;
    // don't re-persist those until the inspector actually picks up the form.
    hasUserInteractedRef.current = false;
    setPendingDraft(null);
    setDraftGate('active');
  };

  const pendingDraftAgeLabel = useMemo(() => {
    if (!pendingDraft) return '';
    const ageMs = Date.now() - pendingDraft.savedAt;
    const mins = Math.round(ageMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }, [pendingDraft]);

  const pendingDraftAnswerCount = pendingDraft
    ? Object.keys(pendingDraft.payload.responses || {}).length
    : 0;

  // Block in-app navigation when inspector has started answering questions
  const inspectionInProgress =
    !showReportStep && Object.keys(responses).length > 0;

  // Report step guard (Jim 2026-08-20): the report exists but was never
  // sent — leaving here used to strand it invisibly. Warn on leave; the
  // explicit Skip/Done buttons set allowLeaveRef so they don't double-prompt.
  // A report on its ten-minute hold (JIM-1003-05) is on its way: leaving is
  // fine, the server sends it.
  const reportUnsent =
    showReportStep && !sendResult && !sendHold && !!submittedInspectionId;
  const allowLeaveRef = useRef(false);

  const blocker = useBlocker(() => {
    if (allowLeaveRef.current) return false;
    return inspectionInProgress || reportUnsent;
  });

  // Block browser close / tab refresh when inspection is in progress or the
  // generated report hasn't been sent yet
  React.useEffect(() => {
    if (!inspectionInProgress && !reportUnsent) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [inspectionInProgress, reportUnsent]);

  // Calculate total visible questions across all categories
  const totalQuestions = useMemo(() => {
    return inspectionCategories.reduce((sum, cat) => {
      return (
        sum +
        cat.questions.filter((q) => isQuestionVisible(q, responses)).length
      );
    }, 0);
  }, [inspectionCategories, responses]);

  // Count only answers for currently visible questions (avoids overcounting stale/hidden responses)
  const answeredQuestions = useMemo(() => {
    const visibleIds = new Set(
      inspectionCategories.flatMap((cat) =>
        cat.questions
          .filter((q) => isQuestionVisible(q, responses))
          .map((q) => q.id)
      )
    );
    return Object.keys(responses).filter((id) => visibleIds.has(id)).length;
  }, [inspectionCategories, responses]);

  // Completed-but-unsubmitted is Jim's P0 silent-data-loss case (2026-07-30):
  // testers answer everything, leave without Review Report, and the
  // inspection sits as a draft forever. When every visible question is
  // answered, the leave modal steers hard toward submitting instead of the
  // neutral Stay/Leave pair.
  const allQuestionsAnswered =
    totalQuestions > 0 && answeredQuestions >= totalQuestions;

  // JIM-1006-12 (Jim 2026-10-06): once Send is tapped the report is on its
  // ten-minute hold and the inspector is done here — "shouldn't it just take
  // me back to Dashboard/Schedule?" Leave for the schedule with the note he
  // asked for; the held report, its clock and Edit live under Needs attention.
  // JIM-1006-05: the phone's position places the inspector at the site and,
  // after filing, notices them leave. Watching stops once the visit is over.
  const sitePin = siteData?.coordinates ?? null;
  useArrivalWatch(
    hasPin(sitePin) ? sitePin : null,
    !visitTiming.left_at,
    (here, at) => {
      if (!hasPin(sitePin)) return;
      setVisitTiming((t) => foldPosition(t, sitePin, here, at));
    }
  );
  // The leave time reaches the filed inspection through its own call; the
  // create payload has already gone by then.
  const leftPostedRef = useRef<string | null>(null);
  // Leaving the flow after filing (Send to Client Later, the dashboard, a
  // closed tab) counts as leaving the site when the phone never said so.
  const timingRef = useRef(visitTiming);
  timingRef.current = visitTiming;
  const filedIdRef = useRef<string | null>(null);
  filedIdRef.current = submittedInspectionId;
  useEffect(() => {
    return () => {
      const id = filedIdRef.current;
      if (!id || timingRef.current.left_at || leftPostedRef.current === id)
        return;
      leftPostedRef.current = id;
      api
        .post(`/inspections/${id}/visit-timing`, {
          left_at: new Date().toISOString(),
        })
        .catch(() => {});
    };
  }, []);
  useEffect(() => {
    if (!visitTiming.left_at || !submittedInspectionId) return;
    if (leftPostedRef.current === submittedInspectionId) return;
    leftPostedRef.current = submittedInspectionId;
    api
      .post(`/inspections/${submittedInspectionId}/visit-timing`, {
        left_at: visitTiming.left_at,
      })
      .catch(() => {
        leftPostedRef.current = null;
      });
  }, [visitTiming.left_at, submittedInspectionId]);
  const leftForHoldRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sendHold?.sendsAt || !submittedInspectionId) return;
    if (leftForHoldRef.current === submittedInspectionId) return;
    leftForHoldRef.current = submittedInspectionId;
    allowLeaveRef.current = true;
    setVisitTiming((t) => markLeft(t, new Date().toISOString()));
    toast({
      title: `The report will be emailed at ${format(new Date(sendHold.sendsAt), 'h:mm a')}.`,
      description:
        'To edit it before then, open Needs attention on your dashboard — editing resets the clock. Leave it and it sends when the timer runs out.',
      duration: HOLD_NOTE_MS,
    });
    // Jim 2026-10-08: after an inspection, the dashboard, not the schedule.
    navigate('/lite');
    // navigate and toast are stable; the hold is what matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendHold?.sendsAt, submittedInspectionId]);

  const submitInspection = async () => {
    if (!siteId) {
      setSubmitError('No site selected');
      return;
    }
    // Set when the inspector came back via Edit after Review Report: the
    // same inspection is revised, never duplicated.
    const revisingInspectionId = submittedInspectionId;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Extract weather conditions from responses
      const weatherResponse = responses['sc-3'];
      const weatherConditions = weatherResponse
        ? {
            conditions: weatherResponse.value as string,
            temperature: undefined,
            rainfall_24h: undefined,
            wind_speed: undefined,
          }
        : undefined;

      // Transform category responses into findings
      // IMPORTANT: Include ALL questions from schema, not just those with current responses
      // This ensures Section 4 of the report shows all 19 questions with their answers
      // Use current response if available, otherwise fall back to previous answer
      // JIM-1008-06: one corrective action for several No answers — which
      // questions each carrying question also covers.
      const allQuestions = inspectionCategories.flatMap((c) =>
        c.questions.map((q) => ({ id: q.id, question: q.question }))
      );
      const questionText = new Map(allQuestions.map((q) => [q.id, q.question]));
      const covered = coveredQuestions(responses, allQuestions);
      const findings = inspectionCategories.flatMap((category) => {
        return category.questions
          .filter((q) => q.type !== 'photo')
          .flatMap((question) => {
            const response = responses[question.id];
            const previousValue = previousAnswers[question.id];

            // Use current response if exists, otherwise use previous answer.
            // If neither exists, default to 'na' so unanswered questions render
            // as N/A in the report (never silently inferred as NO).
            const value =
              response?.value !== undefined
                ? response.value
                : previousValue !== undefined
                  ? previousValue
                  : 'na';

            let description = '';
            let condition: 'good' | 'fair' | 'poor' | 'failed' | 'na' = 'na';

            if (typeof value === 'boolean') {
              description = value ? 'Satisfactory' : 'Needs attention';
              condition = value ? 'good' : 'poor';
            } else if (typeof value === 'string') {
              const lowerValue = value.toLowerCase();
              if (lowerValue === 'na' || lowerValue === 'n/a') {
                description = 'N/A';
                condition = 'na';
              } else {
                description = value;
                if (
                  lowerValue.includes('good') ||
                  lowerValue.includes('satisfactory') ||
                  lowerValue === 'yes'
                ) {
                  condition = 'good';
                } else if (
                  lowerValue.includes('poor') ||
                  lowerValue.includes('damaged') ||
                  lowerValue.includes('missing')
                ) {
                  condition = 'poor';
                } else if (
                  lowerValue.includes('failed') ||
                  lowerValue.includes('critical') ||
                  lowerValue.includes('violation')
                ) {
                  condition = 'failed';
                }
              }
            } else if (typeof value === 'number') {
              description = String(value);
            }

            // Build full description including any notes
            let fullDescription = `${question.question}: ${description}`;

            // IR-002: Include multiple corrective actions in description
            if (
              response?.correctiveActions &&
              response.correctiveActions.length > 0
            ) {
              fullDescription += '\n\nCorrective Actions:';
              response.correctiveActions.forEach((action, idx) => {
                fullDescription += `\n${idx + 1}. `;
                if (action.location) fullDescription += `[${action.location}] `;
                if (action.notes) fullDescription += action.notes;
                if (action.deadline)
                  fullDescription += ` (Due: ${new Date(action.deadline).toLocaleDateString()})`;
              });
            } else if (response?.notes) {
              // Legacy single notes
              fullDescription += `\n\nInspector Notes: ${response.notes}`;
            }

            // IR-002: Get primary location from first corrective action or legacy location field
            const primaryLocation =
              response?.correctiveActions &&
              response.correctiveActions.length > 0
                ? response.correctiveActions[0].location || 'Site'
                : response?.location || 'Site';

            // IR-002: Get primary deadline from first corrective action or legacy field
            const primaryDeadline =
              response?.correctiveActions &&
              response.correctiveActions.length > 0
                ? response.correctiveActions[0].deadline || null
                : response?.correctionDeadline || null;

            // IR-002: Get primary notes from first corrective action or legacy field
            const primaryNotes =
              response?.correctiveActions &&
              response.correctiveActions.length > 0
                ? response.correctiveActions[0].notes || null
                : response?.notes || null;

            // "Carried forward": a No answer that only carries an already-open
            // CA (none added this inspection). Flag it so the backend leaves the
            // carried CA untouched instead of re-creating a duplicate
            // (Jim, 2026-06).
            const hasNewCA = (response?.correctiveActions?.length ?? 0) > 0;
            const hasCarriedOpenCA = (
              correctiveActionsData?.corrective_actions ?? []
            ).some(
              (ca) => ca.question_id === question.id && ca.status === 'open'
            );
            const baseFinding = {
              id: generateId(),
              category: question.category,
              item: question.question,
              condition,
              description: fullDescription,
              location: primaryLocation,
              photo_ids: [] as string[],
              requires_correction: response?.requiresCorrection || false,
              correction_deadline: primaryDeadline,
              correction_notes: primaryNotes,
              corrective_actions: response?.correctiveActions || [],
              source_checklist_question_id: question.id,
              source: 'lite_inspection',
              carried_forward: value === false && !hasNewCA && hasCarriedOpenCA,
            };

            // JIM-1008-06: a No that points at another question files with no
            // corrective action of its own; the carrying question's notes say
            // what else it covers.
            const sameAs =
              value === false && response?.sameAsQuestionId
                ? (questionText.get(response.sameAsQuestionId) ??
                  response.sameAsQuestionId)
                : null;
            if (sameAs) {
              return [
                applySameIssue(
                  { ...baseFinding, corrective_actions: [] },
                  question.question,
                  { sameAs }
                ),
              ];
            }
            const covers = covered[question.id] ?? [];
            // Backend FindingSchema drops nested `corrective_actions` array, and
            // sync_corrective_actions creates one CA row per finding. Emit one
            // finding per CA so each reaches the report as a separate row.
            const cas = response?.correctiveActions || [];
            if (cas.length <= 1) {
              return [
                applySameIssue(baseFinding, question.question, { covers }),
              ];
            }
            return cas.map((ca, idx) => {
              const split = {
                ...baseFinding,
                id: idx === 0 ? baseFinding.id : generateId(),
                location: ca.location || 'Site',
                correction_deadline: ca.deadline || null,
                correction_notes: ca.notes || null,
                description:
                  idx === 0
                    ? fullDescription
                    : `${question.question}: ${ca.notes || 'Needs attention'}`,
              };
              return idx === 0
                ? applySameIssue(split, question.question, { covers })
                : split;
            });
          });
      });

      // notes maps to the legacy `inspection.notes` column; the universal
      // Inspection Report Comment now flows separately via `report_comment`.
      const notesResponse = responses['c-3'];
      const notes = (notesResponse?.value as string) || '';

      // Collect photo data for offline storage
      const photoQuestions = inspectionCategories.flatMap((category) =>
        category.questions.filter(
          (q) => q.type === 'photo' && responses[q.id]?.value
        )
      );

      const photoData: Array<{ localId: string; blob: Blob; metadata: any }> =
        [];
      for (const question of photoQuestions) {
        const response = responses[question.id];
        if (response?.value && typeof response.value === 'string') {
          // Convert base64 to blob for offline storage
          const dataUrl = response.value;
          const res = await fetch(dataUrl);
          const blob = await res.blob();
          photoData.push({
            localId: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            blob,
            metadata: {
              caption: question.question,
              category: question.category,
            },
          });
        }
      }

      // Persist the raw Yes/No/NA answers as checklist_responses. The create
      // endpoint has stored these since Phase 2, but the Lite flow never sent
      // them — checklist_responses stayed empty forever and "previous
      // answers" pre-fill had nothing to read (audit 2026-08-31).
      const checklistResponses = inspectionCategories
        .flatMap((category) => category.questions)
        .map((question) => {
          const r = responses[question.id];
          if (!r) return null;
          let responseValue: 'yes' | 'no' | 'na' | null = null;
          if (r.value === true) responseValue = 'yes';
          else if (r.value === false) responseValue = 'no';
          else if (
            typeof r.value === 'string' &&
            ['na', 'n/a'].includes(r.value.toLowerCase())
          )
            responseValue = 'na';
          if (!responseValue) return null;
          return {
            question_id: question.id,
            response_value: responseValue,
            // JIM-1008-06: a linked No says so on the report row.
            notes:
              r?.value === false && r?.sameAsQuestionId
                ? `Same issue as: ${questionText.get(r.sameAsQuestionId) ?? r.sameAsQuestionId}`
                : r.notes || undefined,
          };
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);

      // Build the inspection data for offline storage
      // JIM-1006-05: filing stamps the visit; a phone that never placed the
      // inspector at the site falls back to when the form was opened.
      const filedTiming = markFiled(visitTiming, new Date().toISOString());
      setVisitTiming(filedTiming);

      const inspectionData = {
        id: '', // Will be set by server
        siteId,
        data: {
          type: visitType(),
          scheduled_date: new Date().toISOString(),
          weather_conditions: weatherConditions,
          findings,
          checklist_responses: checklistResponses,
          notes,
          visit_timing: timingPayload(filedTiming),
          schema_id: schema?.id,
          schema_version: schema?.version,
          // Include construction stage if selected during stage check
          construction_stage_id:
            selectedStageId || currentStage?.id || undefined,
        },
        photos: photoData,
        created: Date.now(),
        lastModified: Date.now(),
        syncStatus: 'pending' as const,
      };

      // STEP 1: Always save to offline storage first (offline-first)
      const localId = await offlineStorage.saveInspection(inspectionData);

      // Save photos to offline storage
      for (const photo of photoData) {
        await offlineStorage.savePhoto(localId, photo.blob, photo.metadata);
      }

      // Everything a background replay needs to finish this inspection the
      // way the online path would: the full payload (not the trimmed
      // offline copy that dropped the conducted date, final flag, consultant
      // alert and report note) plus the follow-up calls the online path
      // makes after create (audit 2026-09-04, C1). Photos are left out: they
      // upload separately and the flow's photo path is dead.
      const replayItem = {
        timestamp: Date.now(),
        method: 'POST' as const,
        url: revisingInspectionId
          ? `/inspections/${revisingInspectionId}/revise`
          : '/inspections/',
        label: siteData?.name || siteId,
        body: {
          site_id: siteId,
          type: visitType(),
          scheduled_date: new Date().toISOString(),
          conducted_date: conductedAtIso(conductedDate, conductedTime),
          is_final_inspection: isFinalInspection,
          final_inspection_note: isFinalInspection
            ? finalInspectionNote.trim() || null
            : null,
          alert_consultant: alertConsultant,
          weather_conditions: weatherConditions,
          findings,
          checklist_responses: checklistResponses,
          notes,
          report_comment: reportComment || null,
          austin_notes: austinNotes || null,
          austin_bmp_events: austinTaps,
          austin_bmp_conditions: conditionsPayload(austinConditions),
          visit_timing: timingPayload(filedTiming),
          photos: [] as string[],
          schema_id: schema?.id,
          schema_version: schema?.version,
          construction_stage_id:
            selectedStageId || currentStage?.id || undefined,
          idempotency_key: localId,
        },
        followUps: buildLiteFollowUps({
          siteId,
          verifiedCaIds,
          stageDates,
          stageNames: allStages.map((stage) => stage.stage_name),
        }),
        maxRetries: 5,
      };

      // STEP 2: Try to sync if online
      // Check auth token state before API call
      const authState = useAuthStore.getState();
      const token = authState.accessToken;
      const refreshToken = authState.refreshToken;
      const isAuthenticated = authState.isAuthenticated;

      addDebug(
        `Auth state: authenticated=${isAuthenticated}, hasToken=${!!token}, hasRefresh=${!!refreshToken}`
      );
      if (token) {
        // Log token info (safe preview only)
        const tokenLen = token.length;
        const tokenPreview =
          tokenLen > 20
            ? `${token.substring(0, 10)}...${token.substring(tokenLen - 10)}`
            : '[short]';
        addDebug(`Token length=${tokenLen}, preview=${tokenPreview}`);

        // Check if token looks like a valid JWT (has 3 parts)
        const parts = token.split('.');
        addDebug(`Token parts: ${parts.length} (expected 3 for JWT)`);

        if (parts.length === 3) {
          try {
            // Decode the payload to check expiry
            const payload = JSON.parse(atob(parts[1]));
            const exp = payload.exp;
            const now = Math.floor(Date.now() / 1000);
            const expiresIn = exp - now;
            addDebug(
              `Token expires in ${expiresIn}s (${expiresIn > 0 ? 'valid' : 'EXPIRED'})`
            );

            // Proactively refresh if token is expired or about to expire (< 60s)
            if (expiresIn < 60 && refreshToken) {
              addDebug('Token expired/expiring soon, attempting refresh...');
              try {
                const refreshResponse = await api.post<{
                  access_token: string;
                  refresh_token: string;
                }>('/auth/refresh', { refresh_token: refreshToken });
                const { access_token, refresh_token: newRefresh } =
                  refreshResponse.data;
                useAuthStore.getState().updateTokens(access_token, newRefresh);
                addDebug('Token refreshed successfully!');
              } catch (refreshErr: any) {
                addDebug(`Token refresh FAILED: ${refreshErr.message}`);
                setSubmitError(
                  'Your session has expired. Please log in again.'
                );
                setIsSubmitting(false);
                return;
              }
            }
          } catch (e) {
            addDebug(`Token decode error: ${e}`);
          }
        }
      } else {
        addDebug('WARNING: No access token found!');
        // Try to use refresh token if available
        if (refreshToken) {
          addDebug(
            'No access token but refresh token exists, attempting refresh...'
          );
          try {
            const refreshResponse = await api.post<{
              access_token: string;
              refresh_token: string;
            }>('/auth/refresh', { refresh_token: refreshToken });
            const { access_token, refresh_token: newRefresh } =
              refreshResponse.data;
            useAuthStore.getState().updateTokens(access_token, newRefresh);
            addDebug('Token obtained from refresh!');
          } catch (refreshErr: any) {
            addDebug(`Token refresh FAILED: ${refreshErr.message}`);
            setSubmitError(
              'You are not logged in. Please log in and try again.'
            );
            setIsSubmitting(false);
            return;
          }
        } else {
          addDebug('No refresh token either - user needs to log in');
          setSubmitError('You are not logged in. Please log in and try again.');
          setIsSubmitting(false);
          return;
        }
      }

      if (isOnline) {
        addDebug('Attempting ONLINE submission...');
        let submittedPayload: any = null;
        try {
          // Upload photos first
          addDebug(`Uploading ${photoData.length} photos...`);
          const photoIds: string[] = [];
          for (const photo of photoData) {
            const photoId = await uploadPhotoFromBlob(
              photo.blob,
              siteId,
              photo.metadata.caption
            );
            if (photoId) {
              photoIds.push(photoId);
            }
          }
          addDebug(`Photos uploaded: ${photoIds.length}`);

          // Update findings with photo IDs
          const findingsWithPhotos = findings.map((finding) => ({
            ...finding,
            photo_ids: photoIds.slice(
              0,
              Math.ceil(photoIds.length / findings.length)
            ),
          }));

          // Build API payload
          // JIM-0919-04: date + time as entered (the time defaults to when
          // the inspection was opened), read in the device zone, sent as UTC.
          const conductedDateIso = conductedAtIso(conductedDate, conductedTime);
          const apiPayload = {
            site_id: siteId,
            type: visitType(),
            scheduled_date: new Date().toISOString(),
            conducted_date: conductedDateIso,
            is_final_inspection: isFinalInspection,
            final_inspection_note: isFinalInspection
              ? finalInspectionNote.trim() || null
              : null,
            alert_consultant: alertConsultant,
            weather_conditions: weatherConditions,
            findings: findingsWithPhotos,
            checklist_responses: checklistResponses,
            notes,
            // F-Comment: universal report comment for page 1 of the PDF.
            report_comment: reportComment || null,
            austin_notes: austinNotes || null,
            austin_bmp_events: austinTaps,
            austin_bmp_conditions: conditionsPayload(austinConditions),
            visit_timing: timingPayload(filedTiming),
            photos: photoIds,
            schema_id: schema?.id,
            schema_version: schema?.version,
            // Include construction stage if selected during stage check
            construction_stage_id:
              selectedStageId || currentStage?.id || undefined,
            // Idempotency: reuse the offline local_id so retries / double-taps
            // don't create duplicate inspections on the server.
            idempotency_key: localId,
          };

          // Schema-version guard — refuse to submit if the phone/tab is
          // running against an outdated schema. When the admin edits a
          // schema mid-session, a stale bundle can send a question list
          // that no longer matches what the server expects, which leaves
          // the inspection stuck in draft and generates no report.
          if (schema?.id) {
            try {
              const liveSchema = await api.get<{ id: string; version: number }>(
                `/inspection-schemas/resolve?site_id=${siteId}`
              );
              const liveId = liveSchema.data.id;
              const liveVersion = liveSchema.data.version;
              const idMismatch = liveId && liveId !== schema.id;
              const versionMismatch =
                typeof liveVersion === 'number' &&
                typeof schema.version === 'number' &&
                liveVersion !== schema.version;
              if (idMismatch || versionMismatch) {
                addDebug(
                  `Schema drift: local=${schema.id}@v${schema.version} live=${liveId}@v${liveVersion}`
                );
                setSubmitError(
                  'The inspection schema has changed since this session started. Refresh the page to load the latest questions, then complete the inspection again.'
                );
                setIsSubmitting(false);
                return;
              }
            } catch (schemaCheckErr: any) {
              // Non-fatal — if we can't verify, fall through to submit so a
              // transient network blip doesn't block the inspector.
              addDebug(
                `Schema drift check skipped: ${schemaCheckErr?.message || 'unknown'}`
              );
            }
          }

          submittedPayload = apiPayload;
          addDebug(`Submitting ${findingsWithPhotos.length} findings...`);
          // Edit → Review Report: the visit already exists — replace its
          // answers instead of filing a second inspection (resubmit gap).
          let response;
          try {
            response = revisingInspectionId
              ? await api.post(
                  `/inspections/${revisingInspectionId}/revise`,
                  apiPayload,
                  { timeout: 120000 }
                )
              : await api.post('/inspections/', apiPayload, {
                  timeout: 120000, // 2 minutes - report generation can take 60+ seconds
                });
          } catch (createErr: any) {
            // The server refuses a second visit on the same site within
            // minutes (409, "already filed N minutes ago"). On 10/29 that
            // left Shane tapping Review Report twenty times with nothing
            // sent (Sun City #88). Open that filed visit and carry on.
            const status =
              createErr?.response?.status ?? createErr?.status ?? null;
            // Say what the server said (Joe 11/3: twelve refused revises read
            // "Request failed with status code 409"); a refused revise also
            // says the report is edited from the Hub now.
            const refusal = submitRefusalMessage(
              status,
              createErr?.response?.data?.detail,
              !!revisingInspectionId
            );
            if (refusal && (status !== 409 || revisingInspectionId)) {
              throw new Error(refusal);
            }
            if (status !== 409 || revisingInspectionId) throw createErr;
            const twin = await inspectionService.listInspections({
              site_id: siteId,
              inspector_id: userId,
              size: 1,
              skip_count: true,
            });
            const filed = (
              twin.items as Array<{ id: string }> | undefined
            )?.[0];
            if (!filed) throw createErr;
            addDebug(`Adopted the visit filed moments ago: ${filed.id}`);
            response = { data: filed };
          }
          const result = response.data as any;
          addDebug(`SUCCESS! ID: ${result.id}`);

          trackFlowEvent('inspection_submitted', {
            sessionId: flowSessionRef.current,
            siteId,
            inspectionId: result.id,
            metadata: { findings: findingsWithPhotos.length },
          });

          // Usage telemetry: per-inspection question rollup (never per tap)
          // and the submit itself. express is stamped false — the express
          // mode shortcut is not present in the current Lite flow.
          track('question_answered', {
            site_id: siteId,
            inspection_id: result.id,
            duration_ms: Date.now() - flowStartedAtRef.current,
            total_questions: Object.keys(responses).length,
          });
          track('inspection_submitted', {
            site_id: siteId,
            inspection_id: result.id,
            duration_ms: Date.now() - flowStartedAtRef.current,
            express: false,
            ca_count: Object.keys(verifiedCaIds).length,
          });

          // Mark as synced in offline storage
          await offlineStorage.updateInspection(localId, {
            id: result.id,
            syncStatus: 'synced',
          });

          // The answers stay on the phone, marked as filed, until the report
          // is sent: Edit on the site screen reopens them (Jim, Teams
          // 2026-10-05). A filed draft is never offered as "Resume?", so a
          // fresh inspection on this site does not inherit it.
          if (userId) {
            offlineStorage
              .saveDraft(siteId, userId, draftPayload(result.id))
              .catch(() => {});
          }

          // Resolve CAs checked off in review — runs after sync_corrective_actions
          // so resolved state takes precedence over question-answer-driven reopening
          const toResolve = Object.entries(verifiedCaIds);
          if (toResolve.length > 0) {
            const resolveResults = await Promise.allSettled(
              toResolve.map(([caId, notes]) =>
                api.patch(
                  `/sites/${siteId}/corrective-actions/${caId}/resolve`,
                  {
                    resolution_notes: notes || null,
                    inspection_id: result.id,
                  }
                )
              )
            );
            const failed = resolveResults.filter(
              (r) => r.status === 'rejected'
            );
            if (failed.length > 0) {
              console.error(
                `Failed to resolve ${failed.length} CA(s):`,
                failed
              );
            }
          }

          // Stage rows for dates the inspector entered on stages that have no
          // observation yet — on the first submit AND on Edit → Review (a
          // stage first dated during the edit was dropped before, and the
          // regenerated report kept the old table). Ids come back into
          // stageDates so a later edit PATCHes the row instead of re-creating it.
          const stagesToCreate = planStageCreates(
            stageDates,
            allStages,
            result.id,
            siteId
          );
          if (stagesToCreate.length > 0) {
            const stageResults = await Promise.allSettled(
              stagesToCreate.map((c) =>
                api.post('/extracted-stages', c.payload)
              )
            );
            const created: Array<{ stageName: string; id: string }> = [];
            stageResults.forEach((r, i) => {
              if (r.status === 'fulfilled') {
                const id = (r.value as { data?: { id?: string } })?.data?.id;
                if (id)
                  created.push({ stageName: stagesToCreate[i].stageName, id });
              } else {
                const err = r.reason;
                console.error(
                  `[StageCreation] FAILED for "${stagesToCreate[i].stageName}":`,
                  err?.response?.status,
                  err?.response?.data || err?.message
                );
              }
            });
            if (created.length > 0) {
              setStageDates((prev) => applyStageIds(prev, created));
            }
          }

          // Freeze the inspector's saved signature onto this inspection so the
          // report is an immutable snapshot — it won't change if they later edit
          // their saved signature. Non-fatal: the report generator already falls
          // back to inspection.inspector.signature_url if this didn't land.
          try {
            await api.post(`/inspections/${result.id}/use-saved-signature`);
            addDebug('Inspector signature applied');
          } catch (sigErr: any) {
            addDebug(
              `Signature apply skipped: ${sigErr?.response?.status || sigErr?.message || 'unknown'}`
            );
          }

          // Auto-generate the report immediately
          addDebug('Generating report...');
          setSubmittedInspectionId(result.id);
          setIsGeneratingReport(true);
          setShowReportStep(true);
          setShowSummary(false);

          try {
            const reportResponse = await inspectionService.generateReport(
              result.id
            ); // 2 minute timeout for slow report generation

            addDebug(`Report generated: ${reportResponse.report_url}`);
            track('report_generated', {
              site_id: siteId,
              inspection_id: result.id,
            });
            setReportUrl(reportResponse.report_url);
          } catch (reportErr) {
            console.error('Failed to auto-generate report:', reportErr);
            addDebug(
              'Report generation failed - you can retry from the next screen'
            );
            setReportError(
              'Auto-generation failed. Click the button below to retry.'
            );
          } finally {
            setIsGeneratingReport(false);
          }
        } catch (syncError: any) {
          trackFlowEvent('submit_failed', {
            sessionId: flowSessionRef.current,
            siteId,
            metadata: {
              status: syncError?.response?.status ?? syncError?.xhrStatus,
              message: String(syncError?.message || 'unknown').slice(0, 200),
              isNetworkError: !!syncError?.isNetworkError,
              isTimeout: !!syncError?.isTimeout,
            },
          });
          // The old "fetch probe" that re-POSTed the payload here for
          // diagnostics was removed: it retried the submit without an
          // idempotency key and ignored the result — a double-submit hazard.
          // submit_failed telemetry above carries the diagnosis instead.
          console.error('[InspectionSubmission] Submission failed:', {
            error: syncError,
            code: syncError.originalCode || syncError.code,
            xhrStatus: syncError.xhrStatus,
            isNetworkError: !!syncError.isNetworkError,
            isTimeout: !!syncError.isTimeout,
            hasResponse: !!syncError.response,
            swController: !!globalThis.navigator?.serviceWorker?.controller,
            payloadBytes: submittedPayload
              ? JSON.stringify(submittedPayload).length
              : 0,
          });

          // submittedInspectionId stays null — approve-and-send is unavailable
          // until a successful submit. Show error to user instead of navigating away
          const errorMessage =
            syncError.response?.data?.detail ||
            syncError.message ||
            'Failed to complete the inspection. Please try again.';
          setSubmitError(
            `Completing the inspection failed: ${errorMessage}. Your answers are saved on this device and will be sent automatically when the connection is back; "Retry now" in the banner at the top sends them sooner.`
          );

          // Also queue for offline sync as backup
          await offlineStorage.addToSyncQueue(replayItem);
          addDebug('Queued for background sync as backup');
        }
      } else {
        // Offline - queue the full replay
        addDebug('OFFLINE MODE - queueing for later sync');

        await offlineStorage.addToSyncQueue(replayItem);

        // Offline submit still retires the draft — the inspection now lives
        // in the sync queue; resuming would create a duplicate on reconnect.
        if (userId) {
          offlineStorage.clearDraft(siteId, userId).catch(() => {});
        }

        // No report screen offline: it used to render "Send to Client"
        // against a local id the server had never seen (audit C1). Land on
        // the schedule with a plain message; the Lite banner shows the queue.
        allowLeaveRef.current = true;
        navigate('/lite', {
          state: {
            success: true,
            inspectionId: localId,
            message:
              'Saved on this device. You are offline, so the inspection will be sent and its report generated automatically when the connection is back. Watch the banner at the top for progress.',
          },
        });
      }
    } catch (err) {
      console.error('Failed to save inspection:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to save inspection. Please try again.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to upload photo from blob
  async function uploadPhotoFromBlob(
    blob: Blob,
    siteId: string,
    caption?: string
  ): Promise<string | null> {
    try {
      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_id', siteId);
      formData.append('category', 'inspection');
      if (caption) {
        formData.append('caption', caption);
      }

      const response = await api.post<{ id: string }>(
        '/photos/upload',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      return response.data.id;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      return null;
    }
  }

  // Loading state for schema
  if (schemaLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <Loader2 className="w-12 h-12 text-primary-600 dark:text-primary-400 animate-spin mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Loading Inspection Form
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {isFromCache ? 'Loading from cache...' : 'Fetching questions...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state for schema
  if (schemaError && isMissingProjectType(schemaError.message)) {
    // Robert Keller 2026-10-19: the form cannot load without the site's type;
    // the inspector sets it here instead of being sent to the site page.
    return (
      <ProjectTypePicker
        siteId={siteId!}
        siteName={siteData?.name}
        onSet={() => void refreshSchema()}
      />
    );
  }
  if (schemaError) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 rounded-full bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-danger-600 dark:text-danger-400" />
          </div>
          <p className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            Failed to Load Form
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {schemaError.message}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // No questions available
  if (inspectionCategories.length === 0 || totalQuestions === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-warning-600 dark:text-warning-400" />
          </div>
          <p className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            No Inspection Form Available
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            No inspection schema has been configured for this site.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary-500" />
          <p className="text-lg font-semibold text-slate-900 dark:text-white">
            Generating Inspection Report…
          </p>
          {/* JIM-0919-01: nothing is "complete" yet — the inspector still
              reviews and sends. Only say what is happening. */}
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Building the PDF for you to review.
          </p>
        </div>
      </div>
    );
  }

  // Edit was tapped for an inspection whose answers are not on this phone.
  // Say so rather than open a blank form: completing that would file a
  // second inspection for the same visit.
  if (editUnavailable) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div
          data-testid="edit-unavailable"
          className="max-w-sm text-center space-y-4"
        >
          <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            This inspection can&apos;t be reopened on this device
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Its answers are kept on the phone that completed it until the report
            is sent. Open it there to make changes.
          </p>
          <button
            type="button"
            onClick={() => {
              allowLeaveRef.current = true;
              navigate(`/lite/sites/${siteId}`);
            }}
            className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm"
          >
            Back to the site
          </button>
        </div>
      </div>
    );
  }

  // Report generation step UI
  if (showReportStep && submittedInspectionId) {
    return (
      <>
        <ReportPhase
          submittedInspectionId={submittedInspectionId}
          siteId={siteId ?? undefined}
          isFinalInspection={isFinalInspection}
          isGeneratingReport={isGeneratingReport}
          reportUrl={reportUrl}
          reportError={reportError}
          isSendingReport={isSendingReport}
          sendResult={sendResult}
          sendHold={sendHold}
          stopSend={stopSend}
          generateReport={generateReport}
          approveAndSend={approveAndSend}
          onBackToDraft={() => {
            // Return to the questions form so the inspector can edit responses
            // and re-generate. Keep responses in state; clear the report so
            // they're not confused into thinking the old PDF still applies.
            setShowReportStep(false);
            setReportUrl(null);
            setReportError(null);
          }}
          onDone={(state) => {
            allowLeaveRef.current = true;
            navigate('/lite', { state });
          }}
          onSkip={(state) => {
            allowLeaveRef.current = true;
            navigate('/lite', { state });
          }}
        />
        {/* Leave-guard for the report step (Jim 2026-08-20): the report is
          generated but not sent. The early return above means the questions-
          phase blocker dialog never renders here, so this step needs its own. */}
        {blocker.state === 'blocked' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Report not sent to client yet
                </h2>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                This inspection is complete, but its report has{' '}
                <strong>not</strong> been sent to the client. If you leave now
                it will wait in <strong>Unsent Reports</strong> on your
                dashboard until you send it.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => blocker.reset()}
                  className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm transition-colors"
                >
                  Stay &amp; send to client
                </button>
                <button
                  onClick={() => blocker.proceed()}
                  className="w-full py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Leave — send it later from my dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // BMP-map gate (EP-SWPPP WS-4) — when an updated map is due, the inspector
  // must put it in before anything else. Shown until the shared status query
  // no longer reports it due (upload or override), which BmpMapGate invalidates.
  if (siteId && bmpGateEngaged && !bmpGateDone) {
    return (
      <BmpMapGate siteId={siteId} onContinue={() => setBmpGateDone(true)} />
    );
  }

  // Final-inspection BMP gate — shown at submit time, after the inspector
  // checked "this is the final inspection". Hard requirement: no bypass.
  if (siteId && finalBmpGateOpen && !finalBmpDone) {
    return (
      <BmpMapGate
        siteId={siteId}
        mode="final"
        onBack={() => setFinalBmpGateOpen(false)}
        onContinue={() => {
          setFinalBmpDone(true);
          setFinalBmpGateOpen(false);
          submitInspection();
        }}
      />
    );
  }

  // Past-due Est. Completion gate — after the BMP gate, before signature.
  if (siteId && estGateNeeded && !estGateDone) {
    return (
      <EstCompletionGate
        siteId={siteId}
        siteName={siteData?.name || 'Site'}
        estimatedEndDate={siteData?.estimated_end_date ?? null}
        mode={estPrompt ?? 'past'}
        permitExpirationDate={siteData?.permit_expiration_date}
        onContinue={() => setEstGateDone(true)}
      />
    );
  }

  // First-inspection recipients check — after the site's dates, before the
  // signature, so the contact list is right before anything is filed.
  if (siteId && recipientsData && recipientsGateNeeded && !recipientsGateDone) {
    return (
      <RecipientsGate
        siteId={siteId}
        data={recipientsData}
        onContinue={() => setRecipientsGateDone(true)}
      />
    );
  }

  // Signature gate — front the questions phase until the inspector has a
  // signature on file. Placed after schema-load / submit / report checks so it
  // only ever guards the start of question-answering, and once confirmed those
  // later phases (already past the gate) take precedence on re-render.
  if (!signatureConfirmed) {
    return (
      <SignatureGate
        siteName={siteData?.name || 'Site'}
        isOnline={isOnline}
        onContinue={() => setSignatureConfirmed(true)}
        onBack={() => navigate(-1)}
      />
    );
  }

  return (
    <>
      {/* Resume draft prompt — shown once on mount when a non-stale draft exists */}
      {draftGate === 'prompt' && pendingDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Resume inspection?
              </h2>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              You have an unfinished inspection saved from{' '}
              {pendingDraftAgeLabel} with {pendingDraftAnswerCount} answered{' '}
              question{pendingDraftAnswerCount === 1 ? '' : 's'}. Pick up where
              you left off, or start fresh?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDiscardDraft}
                className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Start fresh
              </button>
              <button
                onClick={handleResumeDraft}
                className="flex-1 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm transition-colors"
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave inspection confirmation modal. Two cases (Jim 2026-07-30 P0):
          - all questions answered → steer hard to submitting: primary action
            runs the same submit path as the footer button
          - answers incomplete → original neutral Stay/Leave prompt */}
      {blocker.state === 'blocked' &&
        (allQuestionsAnswered ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Complete the inspection before you leave?
                </h2>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                All {totalQuestions} questions are answered, but this inspection
                has <strong>not</strong> been completed. Review Report completes
                it and builds the report. If you leave now, no report is
                generated and the inspection stays a draft.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    blocker.reset();
                    submitInspection();
                  }}
                  className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm transition-colors"
                >
                  Review Report
                </button>
                <button
                  onClick={() => blocker.proceed()}
                  className="w-full py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-red-600 dark:text-red-400 font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  Save draft &amp; finish later (shows on your dashboard)
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Leave inspection?
                </h2>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                This inspection has not been completed. Your answers stay saved
                as a draft on this device, and you can pick it up again from
                your dashboard.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => blocker.reset()}
                  className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Stay
                </button>
                <button
                  onClick={() => blocker.proceed()}
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-sm transition-colors"
                >
                  Leave
                </button>
              </div>
            </div>
          </div>
        ))}

      <QuestionsPhase
        siteId={siteId!}
        siteName={siteData?.name || 'Site'}
        inspectionCategories={inspectionCategories}
        responses={responses}
        setResponses={setResponsesFromUser}
        lastAnswers={lastAnswersData?.answers}
        openCorrectiveActions={openCorrectiveActions}
        setOpenCorrectiveActions={setOpenCorrectiveActions}
        correctiveActionsData={correctiveActionsData}
        verifiedCaIds={verifiedCaIds}
        setVerifiedCaIds={setVerifiedCaIdsFromUser}
        looseCAs={looseCAs}
        onReparentLooseCa={reparentLooseCa}
        onVerifyLooseCa={verifyLooseCa}
        answeredQuestions={answeredQuestions}
        totalQuestions={totalQuestions}
        scrollToQuestionId={scrollToQuestionId}
        serverError={submitError}
        onSubmit={() => {
          if (isFinalInspection && !finalBmpDone) {
            setFinalBmpGateOpen(true);
            return;
          }
          submitInspection();
        }}
        onBack={() => navigate(-1)}
        onShowSiteInfo={() => setShowSiteDetailDrawer(true)}
        siteLoading={siteLoading}
        allStages={allStages}
        currentStage={currentStage}
        stageDates={stageDates}
        setStageDates={setStageDates}
        handleStageDateSetAndSave={handleStageDateSetAndSave}
        handleStageDateBlur={handleStageDateBlur}
        reviewOpenActions={(() => {
          const tableActions = (
            correctiveActionsData?.corrective_actions ?? []
          ).filter((a) => a.status === 'open' || a.status === 'in_progress');
          return tableActions.length > 0 ? tableActions : findingsBasedCAs;
        })()}
        caPriorities={caPriorities}
        setCaPriorities={setCaPrioritiesFromUser}
        reportComment={reportComment}
        setReportComment={setReportCommentFromUser}
        conductedDate={conductedDate}
        setConductedDate={setConductedDate}
        conductedTime={conductedTime}
        setConductedTime={setConductedTime}
        isFinalInspection={isFinalInspection}
        setIsFinalInspection={setIsFinalInspection}
        finalInspectionNote={finalInspectionNote}
        setFinalInspectionNote={setFinalInspectionNote}
        alertConsultant={alertConsultant}
        setAlertConsultant={setAlertConsultant}
        clientName={siteData?.client_name ?? null}
        showRainEventToggle={!!siteData?.rainfall_inspection_required}
        austinDetails={isAustinSite(siteData)}
        austinNotes={austinNotes}
        setAustinNotes={setAustinNotesFromUser}
        austinTaps={austinTaps}
        setAustinTaps={setAustinTapsFromUser}
        austinConditions={austinConditions}
        setAustinConditions={setAustinConditionsFromUser}
        austinFiledInspectionId={submittedInspectionId}
        coversRainEvent={coversRainEvent}
        setCoversRainEvent={setCoversRainEvent}
        rainEventStart={rainEventStart}
      />
      {siteId && (
        <SiteDetailDrawer
          siteId={siteId}
          open={showSiteDetailDrawer}
          onClose={() => setShowSiteDetailDrawer(false)}
        />
      )}
    </>
  );
}
